import {
  ExecutionEngineClientError,
  type ExecutionEngineClient,
  type ExecutionEngineOndoDirectBatchRebalanceInput,
  type ExecutionEngineOndoDirectRebalancePlanInput,
  type ExecutionEngineOperation,
} from "@repo/execution-engine-client";
import type { ExecutionHandle } from "~/lib/toast";
import {
  clearStoredExecutionRequestKey,
  type ExecutionRequestContext,
  readOrCreateStoredExecutionRequestContext,
  readOrCreateStoredExecutionRequestKey,
} from "./portfolio-execution-utils";

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/u;
const CAIP19_ERC20_PATTERN = /^eip155:(\d+)\/erc20:(0x[a-fA-F0-9]{40})$/u;
const LEGACY_EVM_ASSET_PATTERN = /^(0x[a-fA-F0-9]{40}):(\d+)$/u;
const POSITIVE_INTEGER_PATTERN = /^(?:[1-9]\d*)$/u;
const NON_NEGATIVE_INTEGER_PATTERN = /^(?:0|[1-9]\d*)$/u;

type EvmAddress = `0x${string}`;
type OndoDirectBatchSellInput = NonNullable<
  ExecutionEngineOndoDirectBatchRebalanceInput["sells"]
>[number];
type OndoDirectBatchBuyInput = NonNullable<
  ExecutionEngineOndoDirectBatchRebalanceInput["buys"]
>[number];

export interface OndoDirectEvmAssetReference {
  readonly assetId: string;
  readonly chainRef?: string | null;
  readonly tokenAddress?: string | null;
}

export interface NormalizedOndoDirectEvmAsset {
  readonly assetId: string;
  readonly chainId: number;
  readonly tokenAddress: EvmAddress;
}

export interface OndoDirectBatchSellPlan {
  readonly amount: string;
  readonly asset: OndoDirectEvmAssetReference;
  readonly minCashOutRaw?: string;
}

export interface OndoDirectBatchBuyPlan {
  readonly asset: OndoDirectEvmAssetReference;
  readonly cashAmount: string;
  readonly minTokenOutRaw?: string;
}

export interface OndoDirectWeightedBuyTarget {
  readonly allocationPercent: number;
  readonly asset: OndoDirectEvmAssetReference;
  readonly minTokenOutRaw?: string;
}

export interface BuildOndoDirectBatchRebalanceInputParams {
  readonly approvalMode?: ExecutionEngineOndoDirectBatchRebalanceInput["approvalMode"];
  readonly buys?: readonly OndoDirectBatchBuyPlan[];
  readonly cashAsset: OndoDirectEvmAssetReference;
  readonly cashInputAmount?: string;
  readonly completionTarget?: ExecutionEngineOndoDirectBatchRebalanceInput["completionTarget"];
  readonly correlationId?: string;
  readonly finality?: ExecutionEngineOndoDirectBatchRebalanceInput["finality"];
  readonly onReorg?: ExecutionEngineOndoDirectBatchRebalanceInput["onReorg"];
  readonly portfolioId: string;
  readonly requestKey: string;
  readonly residualCashPolicy?: ExecutionEngineOndoDirectBatchRebalanceInput["residualCashPolicy"];
  readonly sells?: readonly OndoDirectBatchSellPlan[];
}

export interface BuildCashFundedOndoDirectBatchRebalanceInputParams extends Omit<
  BuildOndoDirectBatchRebalanceInputParams,
  "buys" | "sells"
> {
  readonly targets: readonly OndoDirectWeightedBuyTarget[];
}

function normalizeEvmAddress(
  value: string | null | undefined,
): EvmAddress | null {
  const trimmed = value?.trim();
  if (!trimmed || !EVM_ADDRESS_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed.toLowerCase() as EvmAddress;
}

function readChainIdFromChainRef(
  chainRef: string | null | undefined,
): number | null {
  const trimmed = chainRef?.trim();
  if (!trimmed) {
    return null;
  }

  const match = /^(?:eip155:)?(\d+)$/u.exec(trimmed);
  if (!match?.[1]) {
    return null;
  }

  const parsed = Number.parseInt(match[1], 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function isPositiveIntegerString(
  value: string | null | undefined,
): value is string {
  return typeof value === "string" && POSITIVE_INTEGER_PATTERN.test(value);
}

function normalizeMinOutRaw(
  value: string | undefined,
  label: string,
): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!NON_NEGATIVE_INTEGER_PATTERN.test(value)) {
    throw new Error(
      `Invalid ${label}: expected a non-negative integer raw amount, received ${JSON.stringify(value)}.`,
    );
  }

  return value === "0" ? undefined : value;
}

export function normalizeOndoDirectEvmAsset(
  input: OndoDirectEvmAssetReference,
): NormalizedOndoDirectEvmAsset | null {
  const caipMatch = CAIP19_ERC20_PATTERN.exec(input.assetId.trim());
  if (caipMatch?.[1] && caipMatch[2]) {
    const chainId = Number.parseInt(caipMatch[1], 10);
    if (!Number.isInteger(chainId) || chainId <= 0) {
      return null;
    }

    const tokenAddress = normalizeEvmAddress(caipMatch[2]);
    if (!tokenAddress) {
      return null;
    }

    return {
      assetId: `eip155:${chainId}/erc20:${tokenAddress}`,
      chainId,
      tokenAddress,
    };
  }

  const legacyMatch = LEGACY_EVM_ASSET_PATTERN.exec(input.assetId.trim());
  if (legacyMatch?.[1] && legacyMatch[2]) {
    const chainId = Number.parseInt(legacyMatch[2], 10);
    const tokenAddress = normalizeEvmAddress(legacyMatch[1]);
    if (!Number.isInteger(chainId) || chainId <= 0 || !tokenAddress) {
      return null;
    }

    return {
      assetId: `eip155:${chainId}/erc20:${tokenAddress}`,
      chainId,
      tokenAddress,
    };
  }

  const tokenAddress = normalizeEvmAddress(input.tokenAddress);
  const chainId = readChainIdFromChainRef(input.chainRef);
  if (!tokenAddress || !chainId) {
    return null;
  }

  return {
    assetId: `eip155:${chainId}/erc20:${tokenAddress}`,
    chainId,
    tokenAddress,
  };
}

function normalizeSellLeg(
  leg: OndoDirectBatchSellPlan,
  chainId: number,
): OndoDirectBatchSellInput | null {
  if (!isPositiveIntegerString(leg.amount)) {
    return null;
  }

  const asset = normalizeOndoDirectEvmAsset(leg.asset);
  if (!asset || asset.chainId !== chainId) {
    return null;
  }

  const minCashOutRaw = normalizeMinOutRaw(leg.minCashOutRaw, "minCashOutRaw");
  return {
    assetId: asset.assetId,
    assetTokenAddress: asset.tokenAddress,
    amount: leg.amount,
    ...(minCashOutRaw !== undefined ? { minCashOutRaw } : {}),
  };
}

function normalizeBuyLeg(
  leg: OndoDirectBatchBuyPlan,
  chainId: number,
): OndoDirectBatchBuyInput | null {
  if (!isPositiveIntegerString(leg.cashAmount)) {
    return null;
  }

  const asset = normalizeOndoDirectEvmAsset(leg.asset);
  if (!asset || asset.chainId !== chainId) {
    return null;
  }

  const minTokenOutRaw = normalizeMinOutRaw(
    leg.minTokenOutRaw,
    "minTokenOutRaw",
  );
  return {
    assetId: asset.assetId,
    assetTokenAddress: asset.tokenAddress,
    cashAmount: leg.cashAmount,
    ...(minTokenOutRaw !== undefined ? { minTokenOutRaw } : {}),
  };
}

function sumPositiveIntegerStrings(values: readonly string[]): string {
  return values.reduce((sum, value) => sum + BigInt(value), 0n).toString();
}

export function buildOndoDirectBatchRebalanceInput(
  params: BuildOndoDirectBatchRebalanceInputParams,
): ExecutionEngineOndoDirectBatchRebalanceInput | null {
  const cashAsset = normalizeOndoDirectEvmAsset(params.cashAsset);
  if (!cashAsset) {
    return null;
  }

  const sells = (params.sells ?? []).map((leg) =>
    normalizeSellLeg(leg, cashAsset.chainId),
  );
  const buys = (params.buys ?? []).map((leg) =>
    normalizeBuyLeg(leg, cashAsset.chainId),
  );
  if (sells.some((leg) => !leg) || buys.some((leg) => !leg)) {
    return null;
  }

  const normalizedSells = sells.filter((leg): leg is OndoDirectBatchSellInput =>
    Boolean(leg),
  );
  const normalizedBuys = buys.filter((leg): leg is OndoDirectBatchBuyInput =>
    Boolean(leg),
  );
  if (normalizedSells.length + normalizedBuys.length === 0) {
    return null;
  }

  // Only derive a vault cash pull for buys-only batches. When sells are
  // present, buys are funded by sell proceeds inside the settlement userOp;
  // sending sum(buys) as an explicit cashInputAmount would make the engine
  // pull the full buy notional from vault cash on top of the proceeds.
  const derivedCashInputAmount =
    params.cashInputAmount && isPositiveIntegerString(params.cashInputAmount)
      ? params.cashInputAmount
      : normalizedSells.length === 0 && normalizedBuys.length > 0
        ? sumPositiveIntegerStrings(normalizedBuys.map((leg) => leg.cashAmount))
        : undefined;

  return {
    requestKey: params.requestKey,
    ...(params.correlationId ? { correlationId: params.correlationId } : {}),
    portfolioId: params.portfolioId,
    chainId: cashAsset.chainId,
    cashAssetId: cashAsset.assetId,
    cashTokenAddress: cashAsset.tokenAddress,
    ...(derivedCashInputAmount
      ? { cashInputAmount: derivedCashInputAmount }
      : {}),
    ...(normalizedSells.length > 0 ? { sells: normalizedSells } : {}),
    ...(normalizedBuys.length > 0 ? { buys: normalizedBuys } : {}),
    completionTarget: params.completionTarget ?? "settled",
    ...(params.finality ? { finality: params.finality } : {}),
    ...(params.onReorg ? { onReorg: params.onReorg } : {}),
    residualCashPolicy: params.residualCashPolicy ?? "sweep_to_user",
    approvalMode: params.approvalMode ?? "submit_missing_approvals",
  };
}

function scaledTargetWeights(
  targets: readonly OndoDirectWeightedBuyTarget[],
): readonly { readonly index: number; readonly scaledWeight: bigint }[] {
  return targets.flatMap((target, index) => {
    if (
      !Number.isFinite(target.allocationPercent) ||
      target.allocationPercent <= 0
    ) {
      return [];
    }

    const scaledWeight = BigInt(
      Math.round(target.allocationPercent * 1_000_000),
    );
    return scaledWeight > 0n ? [{ index, scaledWeight }] : [];
  });
}

export function splitCashAmountAcrossWeightedTargets(input: {
  readonly amount: string;
  readonly targets: readonly OndoDirectWeightedBuyTarget[];
}): readonly OndoDirectBatchBuyPlan[] | null {
  if (!isPositiveIntegerString(input.amount)) {
    return null;
  }

  const weightedTargets = scaledTargetWeights(input.targets);
  if (weightedTargets.length === 0) {
    return null;
  }

  const amount = BigInt(input.amount);
  const totalWeight = weightedTargets.reduce(
    (sum, target) => sum + target.scaledWeight,
    0n,
  );
  if (totalWeight <= 0n) {
    return null;
  }

  const split = weightedTargets.map((target) => {
    const weightedAmount = amount * target.scaledWeight;
    return {
      cashAmount: weightedAmount / totalWeight,
      index: target.index,
      remainder: weightedAmount % totalWeight,
    };
  });
  let allocated = split.reduce((sum, target) => sum + target.cashAmount, 0n);
  let residual = amount - allocated;
  const byRemainder = [...split].sort((left, right) => {
    if (left.remainder === right.remainder) {
      return left.index - right.index;
    }

    return left.remainder > right.remainder ? -1 : 1;
  });
  for (const target of byRemainder) {
    if (residual <= 0n) {
      break;
    }

    target.cashAmount += 1n;
    residual -= 1n;
    allocated += 1n;
  }

  const byIndex = new Map(split.map((target) => [target.index, target]));
  const buys = input.targets.flatMap((target, index) => {
    const splitTarget = byIndex.get(index);
    if (!splitTarget || splitTarget.cashAmount <= 0n) {
      return [];
    }

    return [
      {
        asset: target.asset,
        cashAmount: splitTarget.cashAmount.toString(),
        ...(target.minTokenOutRaw !== undefined
          ? { minTokenOutRaw: target.minTokenOutRaw }
          : {}),
      } satisfies OndoDirectBatchBuyPlan,
    ];
  });

  return buys.length > 0 ? buys : null;
}

export function buildCashFundedOndoDirectBatchRebalanceInput(
  params: BuildCashFundedOndoDirectBatchRebalanceInputParams,
): ExecutionEngineOndoDirectBatchRebalanceInput | null {
  const cashInputAmount = params.cashInputAmount;
  if (!isPositiveIntegerString(cashInputAmount)) {
    return null;
  }

  const cashAsset = normalizeOndoDirectEvmAsset(params.cashAsset);
  if (!cashAsset) {
    return null;
  }

  const targets = params.targets.filter((target) => {
    const asset = normalizeOndoDirectEvmAsset(target.asset);
    return (
      asset &&
      asset.chainId === cashAsset.chainId &&
      asset.assetId.toLowerCase() !== cashAsset.assetId.toLowerCase()
    );
  });
  if (targets.length !== params.targets.length) {
    return null;
  }

  const buys = splitCashAmountAcrossWeightedTargets({
    amount: cashInputAmount,
    targets,
  });
  if (!buys) {
    return null;
  }

  return buildOndoDirectBatchRebalanceInput({
    ...params,
    buys,
    cashInputAmount,
  });
}

export interface OndoDirectRebalancePlanTarget {
  readonly allocationPercent: number;
  readonly asset: OndoDirectEvmAssetReference;
}

export interface BuildOndoDirectRebalancePlanInputParams {
  readonly approvalMode?: ExecutionEngineOndoDirectRebalancePlanInput["approvalMode"];
  readonly cashAsset: OndoDirectEvmAssetReference;
  readonly completionTarget?: ExecutionEngineOndoDirectRebalancePlanInput["completionTarget"];
  readonly correlationId?: string;
  readonly portfolioId: string;
  readonly positionAssetIds?: readonly string[];
  readonly requestKey: string;
  readonly residualCashPolicy?: ExecutionEngineOndoDirectRebalancePlanInput["residualCashPolicy"];
  readonly targets: readonly OndoDirectRebalancePlanTarget[];
}

const REBALANCE_PLAN_TOTAL_WEIGHT_BPS = 10_000;
const REBALANCE_PLAN_MAX_TARGETS = 50;
const REBALANCE_PLAN_MAX_POSITIONS = 100;
const REBALANCE_PLAN_TOTAL_PERCENT_TOLERANCE = 0.000001;

export function buildOndoDirectRebalancePlanInput(
  params: BuildOndoDirectRebalancePlanInputParams,
): ExecutionEngineOndoDirectRebalancePlanInput | null {
  const cashAsset = normalizeOndoDirectEvmAsset(params.cashAsset);
  if (!cashAsset) {
    return null;
  }

  const weightedTargets: {
    readonly allocationPercent: number;
    readonly asset: NormalizedOndoDirectEvmAsset;
  }[] = [];
  for (const target of params.targets) {
    if (
      !Number.isFinite(target.allocationPercent) ||
      target.allocationPercent <= 0
    ) {
      continue;
    }

    const asset = normalizeOndoDirectEvmAsset(target.asset);
    if (
      !asset ||
      asset.chainId !== cashAsset.chainId ||
      asset.assetId === cashAsset.assetId
    ) {
      return null;
    }

    weightedTargets.push({
      allocationPercent: target.allocationPercent,
      asset,
    });
  }
  if (
    weightedTargets.length === 0 ||
    weightedTargets.length > REBALANCE_PLAN_MAX_TARGETS
  ) {
    return null;
  }

  const totalAllocationPercent = weightedTargets.reduce(
    (sum, target) => sum + target.allocationPercent,
    0,
  );
  if (
    Math.abs(totalAllocationPercent - 100) >
    REBALANCE_PLAN_TOTAL_PERCENT_TOLERANCE
  ) {
    return null;
  }

  const weightSplit = splitCashAmountAcrossWeightedTargets({
    amount: REBALANCE_PLAN_TOTAL_WEIGHT_BPS.toString(),
    targets: weightedTargets.map((target) => ({
      allocationPercent: target.allocationPercent,
      asset: { assetId: target.asset.assetId },
    })),
  });
  if (!weightSplit) {
    return null;
  }

  const targets = weightSplit.map((target) => ({
    assetId: target.asset.assetId,
    weightBps: Number.parseInt(target.cashAmount, 10),
  }));

  const positions: string[] = [];
  const seenPositionAssetIds = new Set<string>([cashAsset.assetId]);
  for (const positionAssetId of params.positionAssetIds ?? []) {
    const asset = normalizeOndoDirectEvmAsset({ assetId: positionAssetId });
    if (
      !asset ||
      asset.chainId !== cashAsset.chainId ||
      seenPositionAssetIds.has(asset.assetId)
    ) {
      continue;
    }

    seenPositionAssetIds.add(asset.assetId);
    positions.push(asset.assetId);
    if (positions.length >= REBALANCE_PLAN_MAX_POSITIONS) {
      break;
    }
  }

  return {
    requestKey: params.requestKey,
    ...(params.correlationId ? { correlationId: params.correlationId } : {}),
    portfolioId: params.portfolioId,
    chainId: cashAsset.chainId,
    cashAssetId: cashAsset.assetId,
    cashTokenAddress: cashAsset.tokenAddress,
    targets,
    ...(positions.length > 0 ? { positions } : {}),
    completionTarget: params.completionTarget ?? "settled",
    residualCashPolicy: params.residualCashPolicy ?? "sweep_to_user",
    approvalMode: params.approvalMode ?? "submit_missing_approvals",
  };
}

function randomRequestKeySuffix(): string {
  return typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function createOndoDirectBatchRebalanceRequestKey(input: {
  readonly portfolioId: string;
}): string {
  return `ondo-rebalance:${input.portfolioId}:${randomRequestKeySuffix()}`;
}

const ONDO_DIRECT_REBALANCE_REQUEST_KEY_PREFIXES = [
  "ondo-rebalance:",
  "ondo-rebalance-plan:",
] as const;

export function isOndoDirectRebalanceRequestKey(
  value: string | null | undefined,
): boolean {
  return (
    typeof value === "string" &&
    ONDO_DIRECT_REBALANCE_REQUEST_KEY_PREFIXES.some((prefix) =>
      value.startsWith(prefix),
    )
  );
}

export function ondoDirectBatchRebalanceFingerprintParts(
  input: ExecutionEngineOndoDirectBatchRebalanceInput,
): readonly string[] {
  return [
    input.portfolioId,
    input.chainId.toString(),
    input.cashAssetId.toLowerCase(),
    input.cashTokenAddress.toLowerCase(),
    input.cashInputAmount ?? "none",
    ...(input.sells ?? []).flatMap((leg) => [
      "sell",
      leg.assetId.toLowerCase(),
      leg.amount,
      leg.minCashOutRaw ?? "none",
    ]),
    ...(input.buys ?? []).flatMap((leg) => [
      "buy",
      leg.assetId.toLowerCase(),
      leg.cashAmount,
      leg.minTokenOutRaw ?? "none",
    ]),
  ];
}

export function readOrCreateOndoDirectBatchRebalanceRequestKey(
  input: ExecutionEngineOndoDirectBatchRebalanceInput,
): string {
  return readOrCreateStoredExecutionRequestKey({
    namespace: "ondo-direct-batch-rebalance",
    fingerprintParts: ondoDirectBatchRebalanceFingerprintParts(input),
    createRequestKey: () =>
      createOndoDirectBatchRebalanceRequestKey({
        portfolioId: input.portfolioId,
      }),
  });
}

export function clearOndoDirectBatchRebalanceRequestKey(
  input: ExecutionEngineOndoDirectBatchRebalanceInput,
): void {
  clearStoredExecutionRequestKey({
    namespace: "ondo-direct-batch-rebalance",
    fingerprintParts: ondoDirectBatchRebalanceFingerprintParts(input),
  });
}

export async function submitOndoDirectBatchRebalance(params: {
  readonly client: ExecutionEngineClient;
  readonly input: ExecutionEngineOndoDirectBatchRebalanceInput;
}): Promise<ExecutionEngineOperation> {
  return await params.client.createOndoDirectBatchRebalance(params.input);
}

const ONDO_DIRECT_REBALANCE_PLAN_REQUEST_KEY_NAMESPACE =
  "ondo-direct-rebalance-plan";

export function ondoDirectRebalancePlanFingerprintParts(
  input: ExecutionEngineOndoDirectRebalancePlanInput,
): readonly string[] {
  return [
    input.portfolioId,
    input.chainId.toString(),
    input.cashAssetId.toLowerCase(),
    ...input.targets.flatMap((target) => [
      target.assetId.toLowerCase(),
      target.weightBps.toString(),
    ]),
  ];
}

export function createOndoDirectRebalancePlanRequestKey(input: {
  readonly portfolioId: string;
}): string {
  return `ondo-rebalance-plan:${input.portfolioId}:${randomRequestKeySuffix()}`;
}

export function readOrCreateOndoDirectRebalancePlanRequestKey(
  input: ExecutionEngineOndoDirectRebalancePlanInput,
): string {
  return readOrCreateOndoDirectRebalancePlanRequestContext(input).requestKey;
}

export function readOrCreateOndoDirectRebalancePlanRequestContext(
  input: ExecutionEngineOndoDirectRebalancePlanInput,
): ExecutionRequestContext {
  return readOrCreateStoredExecutionRequestContext({
    namespace: ONDO_DIRECT_REBALANCE_PLAN_REQUEST_KEY_NAMESPACE,
    fingerprintParts: ondoDirectRebalancePlanFingerprintParts(input),
    createRequestKey: () =>
      createOndoDirectRebalancePlanRequestKey({
        portfolioId: input.portfolioId,
      }),
  });
}

export function clearOndoDirectRebalancePlanRequestKey(
  input: ExecutionEngineOndoDirectRebalancePlanInput,
): void {
  clearStoredExecutionRequestKey({
    namespace: ONDO_DIRECT_REBALANCE_PLAN_REQUEST_KEY_NAMESPACE,
    fingerprintParts: ondoDirectRebalancePlanFingerprintParts(input),
  });
}

export async function submitOndoDirectRebalancePlan(params: {
  readonly client: ExecutionEngineClient;
  readonly input: ExecutionEngineOndoDirectRebalancePlanInput;
}): Promise<ExecutionEngineOperation> {
  return await params.client.createOndoDirectRebalanceFromTargets(params.input);
}

const EXECUTION_REQUEST_CONFLICT_ERROR = "execution_request_conflict";

export function isExecutionRequestConflictError(
  error: unknown,
): error is ExecutionEngineClientError {
  return (
    error instanceof ExecutionEngineClientError &&
    error.status === 409 &&
    error.details?.error === EXECUTION_REQUEST_CONFLICT_ERROR
  );
}

export function readExecutionRequestConflictOperationId(
  error: unknown,
): string | null {
  if (!isExecutionRequestConflictError(error)) {
    return null;
  }

  const operationId = error.details?.operationId;
  return typeof operationId === "string" && operationId.trim().length > 0
    ? operationId.trim()
    : null;
}

const ONDO_DIRECT_REBALANCE_NO_ACTION_NEEDED_ERROR =
  "ondo_direct_rebalance_no_action_needed";

export function isOndoDirectRebalanceNoActionNeededError(
  error: unknown,
): boolean {
  return (
    error instanceof ExecutionEngineClientError &&
    (error.details?.error === ONDO_DIRECT_REBALANCE_NO_ACTION_NEEDED_ERROR ||
      error.message === ONDO_DIRECT_REBALANCE_NO_ACTION_NEEDED_ERROR)
  );
}

function mapExecutionEngineKindToToastKind(
  operation: ExecutionEngineOperation,
): ExecutionHandle["kind"] {
  if (operation.flow === "ondo_direct_batch_rebalance") {
    return "rebalance";
  }

  if (operation.kind === "withdraw") {
    return "withdraw";
  }

  return "execution";
}

export function mapExecutionEngineOperationToExecutionHandle(
  operation: ExecutionEngineOperation,
): ExecutionHandle {
  const createdAt = operation.createdAt ?? new Date().toISOString();
  const updatedAt = operation.updatedAt ?? createdAt;

  return {
    operationId: operation.operationId,
    portfolioId: operation.portfolioId ?? "",
    kind: mapExecutionEngineKindToToastKind(operation),
    state: operation.state,
    createdAt,
    updatedAt,
    finishedAt: operation.finishedAt ?? null,
    refs: {
      workflowId: operation.workflowId,
      runId: operation.workflowId,
      ...(operation.requestKey ? { requestKey: operation.requestKey } : {}),
    },
    progress: {
      ...(operation.currentPhase ? { phase: operation.currentPhase } : {}),
      stepIndex: operation.currentStep ?? undefined,
      totalSteps: operation.totalSteps ?? undefined,
    },
    error: operation.error?.message ?? null,
    nextSteps: {
      poll: {
        procedure: "v2.executions.get",
        input: { operationId: operation.operationId },
      },
      stream: {
        procedure: "v2.executions.stream",
        input: { operationId: operation.operationId },
      },
    },
    nextRecommendedCommands: [],
  };
}
